import { Dispatch, SetStateAction } from 'react'

import { Alert, Box, Text } from '@factorialco/deprecated-design-system'
import { TimeBalanceExportSeeEverybodyDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import {
  dateBackendToUI,
  dateToString,
  dateUIToBackend,
  stringToDate,
} from 'modules/core/lib/dates'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'

import type ExportYearlyBalanceForm from 'modules/shift_management/lib/forms/ExportYearlyBalanceForm'
import { maxDate, minDate } from 'modules/shift_management/lib/getMinMaxDates'

import TeamsLocationsSelector from '../TeamsLocationsSelector'
import { getDaysUntilNextYear } from './getDaysUntilNextYear'

type IdsTypes = number[] | null
type DateValues = [string | undefined, string | undefined]

interface Props {
  form: ExportYearlyBalanceForm
  selectedTeamIds: IdsTypes
  selectedLocationIds: IdsTypes
  setTeamsIds: Dispatch<SetStateAction<number[]>>
  setLocationsIds: Dispatch<SetStateAction<number[]>>
  hasSelectedTeamOrLocation: boolean
  setLocationsIdsSelection: (ids: number[]) => void
  setTeamsIdsSelection: (ids: number[]) => void
}

const FormContent = ({
  form,
  selectedTeamIds,
  selectedLocationIds,
  setTeamsIds,
  setLocationsIds,
  hasSelectedTeamOrLocation,
  setLocationsIdsSelection,
  setTeamsIdsSelection,
}: Props) => {
  const { data: permissions, loading: loadingPermissions } = useQuery(
    TimeBalanceExportSeeEverybodyDocument,
    {}
  )
  const canExportTimeBalanceForEverybody =
    permissions?.timeBalance._policies?.exportSeeEverybody.can ?? false

  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()

  const startDate = form.get('start_date')
  const endDate = form.get('end_date')

  const maxDateToExport = () => {
    if (!startDate.value) return maxDate

    const daysUntilNextYear = getDaysUntilNextYear({ date: startDate.value, format: 'ui' })

    if (daysUntilNextYear < 7) return stringToDate(startDate.value, 'ui').endOf('year')

    return maxDate
  }

  const onDateChange = (values: DateValues) => {
    const [startValue] = values

    if (!startValue) return

    const daysUntilNextYear = getDaysUntilNextYear({ date: startValue, format: 'backend' })

    const addWeek =
      daysUntilNextYear < 7
        ? dateToString(stringToDate(startValue, 'backend').endOf('year'), 'backend')
        : dateToString(stringToDate(startValue, 'backend').add(6, 'day'), 'backend')

    const start = dateBackendToUI(startValue)
    const end = dateBackendToUI(addWeek)

    startDate.set(start)
    endDate.set(end)
  }

  if (loadingPermissions) {
    return <Loading />
  }

  return (
    <Box gap="s24">
      <TeamsLocationsSelector
        locationsIds={selectedLocationIds}
        setLocationsIds={setLocationsIds}
        teamsIds={selectedTeamIds}
        setTeamsIds={setTeamsIds}
        hasSelectedTeamOrLocation={hasSelectedTeamOrLocation}
        canManageAllCompany={canExportTimeBalanceForEverybody}
        setLocationsIdsSelection={setLocationsIdsSelection}
        setTeamsIdsSelection={setTeamsIdsSelection}
      />
      <DayPicker
        mode="range"
        label={i18n.t('shift_management.export_annual_balance.modal.week_title')}
        description={i18n.t('shift_management.export_annual_balance.modal.week_description')}
        placeholder={i18n.t('common.select')}
        onChange={onDateChange}
        value={[
          startDate.value ? dateUIToBackend(startDate.value) : undefined,
          endDate.value ? dateUIToBackend(endDate.value) : undefined,
        ]}
        firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
        maxDate={maxDateToExport()}
        minDate={minDate}
        mandatory
      />
      <Alert color="info">
        <Text.Normal>{i18n.t('shift_management.export_annual_balance.modal.alert_2')}</Text.Normal>
      </Alert>
    </Box>
  )
}

export default observer(FormContent)
